/* ============================================
   NuevaEmpresa — Alta de empresa/persona por pasos (vista completa)
   Paso 1: Identidad (tipo, nombre, documento, autocompletado SUNAT)
   Paso 2: Contacto (datos de contacto y direccion)
   Paso 3: Confirmar y crear
   Mismo patron visual que NuevoPartner.
   ============================================ */

import { useState, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import type { TipoEntidad, TipoDocumentoFiscal, Contacto } from '@/lib/types'
import { PageHeader } from '@/components/ui'

const STEPS = ['Identidad', 'Contacto', 'Confirmar']

export default function NuevaEmpresa() {
  const api = useApi()
  const navigate = useNavigate()
  const enPartner = useLocation().pathname.startsWith('/partner')
  const base = enPartner ? '/partner/empresas' : '/clientes'

  const [step, setStep] = useState(0)
  const [saving, setSaving] = useState(false)
  const [buscandoRuc, setBuscandoRuc] = useState(false)

  // ─── Contacto principal: buscar en Contactos existentes o crear uno nuevo ───
  const [contactoResults, setContactoResults] = useState<Contacto[]>([])
  const [showContactoDrop, setShowContactoDrop] = useState(false)
  const [contactoMode, setContactoMode] = useState<'nuevo' | 'existente'>('nuevo')
  const [contactoId, setContactoId] = useState<number | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [form, setForm] = useState({
    tipo_entidad: 'empresa' as TipoEntidad,
    empresa: '',
    tipo_documento_fiscal: 'RUC' as TipoDocumentoFiscal,
    ruc_dni: '',
    razon_social: '',
    direccion_fiscal: '',
    ubigeo: '',
    pais: 'PE',
    es_extranjero: false,
    contacto: '',
    email: '',
    telefono: '',
    direccion: '',
  })

  const set = (patch: Partial<typeof form>) => setForm(f => ({ ...f, ...patch }))

  // ─── Autocompletado desde SUNAT ───
  async function buscarRuc() {
    const ruc = form.ruc_dni.trim()
    if (!/^\d{11}$/.test(ruc)) { showToast('Ingresa un RUC válido de 11 dígitos', 'error'); return }
    setBuscandoRuc(true)
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'
      const res = await fetch(`${API_URL}/api/external/lookup-ruc?ruc=${ruc}`)
      const data = await res.json()
      if (!data.ok) { showToast(data.message || 'No se pudo consultar el RUC', 'error'); return }

      set({
        razon_social: data.razon_social || form.razon_social,
        direccion_fiscal: data.direccion || form.direccion_fiscal,
        ubigeo: data.ubigeo || form.ubigeo,
        empresa: form.empresa || data.razon_social || '',
      })

      if (data.estado && data.estado !== 'ACTIVO') showToast(`Estado SUNAT: ${data.estado}`, 'error')
      else if (data.condicion && data.condicion !== 'HABIDO') showToast(`Condición SUNAT: ${data.condicion}`, 'error')
      else showToast('Datos fiscales cargados desde SUNAT', 'success')
    } catch {
      showToast('Error de conexión al consultar SUNAT', 'error')
    } finally {
      setBuscandoRuc(false)
    }
  }

  // ─── Combobox de contacto principal ───
  function onContactoChange(v: string) {
    set({ contacto: v })
    // Cualquier edición manual rompe el vínculo previo: vuelve a modo "nuevo".
    setContactoMode('nuevo')
    setContactoId(null)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const q = v.trim()
    if (q.length < 2) { setContactoResults([]); setShowContactoDrop(false); return }
    debounceRef.current = setTimeout(async () => {
      try {
        const rows = await api.get<Contacto[]>(`/api/contactos?search=${encodeURIComponent(q)}`)
        setContactoResults(rows.slice(0, 6))
        setShowContactoDrop(true)
      } catch { /* búsqueda silenciosa: no interrumpe el alta */ }
    }, 300)
  }

  function elegirContacto(ct: Contacto) {
    const nombre = [ct.nombre, ct.apellido].filter(Boolean).join(' ')
    set({ contacto: nombre, email: ct.email || '', telefono: ct.telefono || '' })
    setContactoId(ct.id)
    setContactoMode('existente')
    setShowContactoDrop(false)
    setContactoResults([])
  }

  function limpiarContacto() {
    set({ contacto: '', email: '', telefono: '' })
    setContactoId(null)
    setContactoMode('nuevo')
    setContactoResults([])
    setShowContactoDrop(false)
  }

  // ─── Validacion por paso ───
  function validarIdentidad(): boolean {
    if (!form.empresa.trim()) {
      showToast(form.tipo_entidad === 'persona' ? 'El nombre es requerido' : 'El nombre de la empresa es requerido', 'error')
      return false
    }
    if (form.tipo_documento_fiscal === 'RUC' && form.ruc_dni.trim() && !/^\d{11}$/.test(form.ruc_dni.trim())) {
      showToast('El RUC debe tener 11 dígitos', 'error'); return false
    }
    if (form.tipo_documento_fiscal === 'DNI' && form.ruc_dni.trim() && !/^\d{8}$/.test(form.ruc_dni.trim())) {
      showToast('El DNI debe tener 8 dígitos', 'error'); return false
    }
    return true
  }

  function validarContacto(): boolean {
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      showToast('El email no tiene un formato valido', 'error'); return false
    }
    return true
  }

  function siguiente() {
    if (step === 0 && !validarIdentidad()) return
    if (step === 1 && !validarContacto()) return
    setStep(s => Math.min(s + 1, STEPS.length - 1))
  }

  function atras() { setStep(s => Math.max(s - 1, 0)) }

  async function crear() {
    if (!validarIdentidad() || !validarContacto()) return
    setSaving(true)
    try {
      // Contacto principal: vincula uno existente o crea uno nuevo en Contactos.
      const nombreContacto = form.contacto.trim()
      const contacto_principal = !nombreContacto ? undefined
        : contactoMode === 'existente' && contactoId
          ? { modo: 'existente', contacto_id: contactoId }
          : { modo: 'nuevo', nombre: nombreContacto, email: form.email.trim() || null, telefono: form.telefono.trim() || null }

      // partner_id lo asigna el backend segun el rol; aqui no se envia.
      await api.post('/api/clientes', {
        empresa: form.empresa.trim(),
        tipo_entidad: form.tipo_entidad,
        tipo_documento_fiscal: form.tipo_documento_fiscal,
        ruc_dni: form.ruc_dni.trim() || null,
        razon_social: form.razon_social.trim() || null,
        direccion_fiscal: form.direccion_fiscal.trim() || null,
        ubigeo: form.ubigeo.trim() || null,
        pais: form.pais || 'PE',
        es_extranjero: form.es_extranjero,
        direccion: form.direccion.trim() || null,
        // El contacto va SOLO por la relación (contactos + contacto_empresa).
        contacto_principal,
      })
      showToast(form.tipo_entidad === 'persona' ? 'Persona creada' : 'Empresa creada', 'success')
      navigate(base)
    } catch {
      showToast('Error al crear', 'error')
    } finally {
      setSaving(false)
    }
  }

  const esPersona = form.tipo_entidad === 'persona'

  return (
    <div className="page-enter" style={{ maxWidth: 780, margin: '0 auto' }}>
      {/* Header */}
      <PageHeader breadcrumbs={[{ label: 'Empresas', to: base }, { label: 'Nueva' }]} />

      {/* Stepper */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 24 }}>
        {STEPS.map((label, i) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', flex: i < STEPS.length - 1 ? 1 : '0 0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                width: 28, height: 28, borderRadius: '50%', display: 'grid', placeItems: 'center',
                fontSize: '0.82rem', fontWeight: 700, flexShrink: 0,
                background: i <= step ? 'var(--primary-light)' : 'var(--bg-card-hover)',
                color: i <= step ? 'var(--text-on-primary)' : 'var(--text-muted)',
                border: i === step ? '2px solid var(--primary-light)' : '2px solid transparent',
              }}>{i < step ? '✓' : i + 1}</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: i <= step ? 'var(--text-primary)' : 'var(--text-muted)' }}>{label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 2, margin: '0 12px', background: i < step ? 'var(--primary-light)' : 'var(--border)' }} />
            )}
          </div>
        ))}
      </div>

      {/* Contenido del paso */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '1.5rem', background: 'var(--bg-card)', marginBottom: 20 }}>

        {/* PASO 1 — Identidad */}
        {step === 0 && (
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: '1.05rem' }}>¿A quien vas a registrar?</h3>
            <p style={{ margin: '0 0 16px', color: 'var(--text-secondary)', fontSize: '0.88rem' }}>
              Si es peruana y tienes su RUC, puedes traer los datos fiscales desde SUNAT.
            </p>

            {/* Tipo de entidad */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
              {([['empresa', 'Empresa'], ['persona', 'Persona / Freelancer']] as const).map(([t, label]) => (
                <button key={t} type="button"
                  onClick={() => set({ tipo_entidad: t, tipo_documento_fiscal: t === 'persona' ? 'DNI' : 'RUC' })}
                  style={{
                    flex: 1, textAlign: 'left', padding: '12px 14px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                    border: form.tipo_entidad === t ? '2px solid var(--primary-light)' : '1px solid var(--border)',
                    background: form.tipo_entidad === t ? 'color-mix(in srgb, var(--primary-light) 10%, transparent)' : 'transparent',
                    color: 'var(--text-primary)', fontWeight: 600,
                  }}>
                  {label}
                </button>
              ))}
            </div>

            <div className="form-group">
              <label className="form-label">{esPersona ? 'Nombre completo *' : 'Nombre comercial *'}</label>
              <input className="form-input" value={form.empresa} autoFocus
                onChange={e => set({ empresa: e.target.value })} />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Tipo de documento</label>
                <select className="form-input" value={form.tipo_documento_fiscal}
                  onChange={e => set({ tipo_documento_fiscal: e.target.value as TipoDocumentoFiscal })}>
                  <option value="RUC">RUC</option>
                  <option value="DNI">DNI</option>
                  <option value="OTRO">Otro / Extranjero</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Número</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="form-input" value={form.ruc_dni}
                    onChange={e => set({ ruc_dni: e.target.value })} />
                  {form.tipo_documento_fiscal === 'RUC' && (
                    <button type="button" className="btn btn-secondary" onClick={buscarRuc} disabled={buscandoRuc}>
                      {buscandoRuc ? '...' : 'SUNAT'}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Razón social</label>
              <input className="form-input" value={form.razon_social}
                onChange={e => set({ razon_social: e.target.value })} />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Dirección fiscal</label>
                <input className="form-input" value={form.direccion_fiscal}
                  onChange={e => set({ direccion_fiscal: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Ubigeo</label>
                <input className="form-input" value={form.ubigeo}
                  onChange={e => set({ ubigeo: e.target.value })} />
              </div>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.es_extranjero}
                onChange={e => set({ es_extranjero: e.target.checked, pais: e.target.checked ? '' : 'PE' })} />
              Es extranjero (fuera de Perú)
            </label>

            {form.es_extranjero && (
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">País</label>
                <input className="form-input" value={form.pais} onChange={e => set({ pais: e.target.value })} />
              </div>
            )}
          </div>
        )}

        {/* PASO 2 — Contacto */}
        {step === 1 && (
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: '1.05rem' }}>Datos de contacto</h3>
            <p style={{ margin: '0 0 16px', color: 'var(--text-secondary)', fontSize: '0.88rem' }}>
              Opcional. Busca un contacto que ya exista o crea uno nuevo: quedará vinculado como
              contacto principal de la empresa (aparece luego en Contactos).
            </p>

            <div className="form-group" style={{ position: 'relative' }}>
              <label className="form-label">Persona de contacto</label>
              <input className="form-input" value={form.contacto} autoFocus autoComplete="off"
                placeholder="Escribe un nombre para buscar o crear…"
                onChange={e => onContactoChange(e.target.value)}
                onFocus={() => { if (contactoResults.length) setShowContactoDrop(true) }}
                onBlur={() => setTimeout(() => setShowContactoDrop(false), 150)} />

              {/* Dropdown de resultados */}
              {showContactoDrop && (contactoResults.length > 0 || form.contacto.trim().length >= 2) && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: 4,
                  background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                  boxShadow: '0 8px 24px rgba(var(--shadow-rgb), calc(.25 * var(--shadow-k)))', overflow: 'hidden',
                }}>
                  {contactoResults.map(ct => (
                    <button key={ct.id} type="button"
                      onMouseDown={e => e.preventDefault()} onClick={() => elegirContacto(ct)}
                      style={{
                        display: 'flex', width: '100%', alignItems: 'center', gap: 10, padding: '10px 12px',
                        background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', color: 'var(--text-primary)',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-card-hover)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                      <span style={{
                        width: 30, height: 30, flexShrink: 0, borderRadius: '50%', display: 'grid', placeItems: 'center',
                        background: 'var(--primary-light)', color: 'var(--text-on-primary)', fontSize: '0.82rem', fontWeight: 700,
                      }}>{ct.nombre.charAt(0).toUpperCase()}</span>
                      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>{[ct.nombre, ct.apellido].filter(Boolean).join(' ')}</span>
                        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ct.email || ct.telefono || 'Sin datos de contacto'}
                        </span>
                      </span>
                    </button>
                  ))}
                  {form.contacto.trim().length >= 2 && (
                    <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => setShowContactoDrop(false)}
                      style={{
                        display: 'flex', width: '100%', alignItems: 'center', gap: 8, padding: '10px 12px',
                        background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
                        borderTop: contactoResults.length ? '1px solid var(--border)' : 'none',
                        color: 'var(--primary-light)', fontWeight: 600, fontSize: '0.85rem',
                      }}>
                      ＋ Crear "{form.contacto.trim()}" como contacto nuevo
                    </button>
                  )}
                </div>
              )}

              {/* Badge de estado del vínculo */}
              {form.contacto.trim() && (
                contactoMode === 'existente' ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: '0.8rem',
                    color: 'var(--text-secondary)',
                  }}>
                    <span style={{ color: 'var(--success, #16a34a)' }}>🔗 Se vinculará un contacto existente.</span>
                    <button type="button" onClick={limpiarContacto}
                      style={{ background: 'none', border: 'none', color: 'var(--primary-light)', cursor: 'pointer', fontWeight: 600, padding: 0 }}>
                      cambiar
                    </button>
                  </div>
                ) : (
                  <div style={{ marginTop: 8, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    ✨ Se creará un contacto nuevo y quedará vinculado a esta empresa.
                  </div>
                )
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={form.email}
                  disabled={contactoMode === 'existente'}
                  onChange={e => set({ email: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Teléfono</label>
                <input className="form-input" value={form.telefono}
                  disabled={contactoMode === 'existente'}
                  onChange={e => set({ telefono: e.target.value })} />
              </div>
            </div>
            {contactoMode === 'existente' && (
              <p style={{ margin: '-8px 0 12px', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                Email y teléfono provienen del contacto elegido. Para editarlos, ve al módulo Contactos.
              </p>
            )}

            <div className="form-group">
              <label className="form-label">Dirección</label>
              <input className="form-input" value={form.direccion}
                onChange={e => set({ direccion: e.target.value })} />
            </div>
          </div>
        )}

        {/* PASO 3 — Confirmar */}
        {step === 2 && (
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: '1.05rem' }}>Revisa antes de crear</h3>
            <p style={{ margin: '0 0 16px', color: 'var(--text-secondary)', fontSize: '0.88rem' }}>
              Podras editar cualquiera de estos datos despues.
            </p>

            <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', margin: 0, fontSize: '0.88rem' }}>
              <dt style={{ color: 'var(--text-secondary)' }}>Tipo</dt>
              <dd style={{ margin: 0, fontWeight: 600 }}>{esPersona ? 'Persona / Freelancer' : 'Empresa'}</dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Nombre</dt>
              <dd style={{ margin: 0, fontWeight: 600 }}>{form.empresa}</dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Documento</dt>
              <dd style={{ margin: 0 }}>{form.ruc_dni ? `${form.tipo_documento_fiscal} ${form.ruc_dni}` : '—'}</dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Razón social</dt>
              <dd style={{ margin: 0 }}>{form.razon_social || '—'}</dd>
              <dt style={{ color: 'var(--text-secondary)' }}>País</dt>
              <dd style={{ margin: 0 }}>{form.es_extranjero ? (form.pais || 'Extranjero') : 'Perú'}</dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Contacto</dt>
              <dd style={{ margin: 0 }}>{form.contacto || '—'}</dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Email</dt>
              <dd style={{ margin: 0 }}>{form.email || '—'}</dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Teléfono</dt>
              <dd style={{ margin: 0 }}>{form.telefono || '—'}</dd>
            </dl>

            {enPartner && (
              <p style={{ marginTop: 16, fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                Esta empresa quedara en tu cartera y solo tu la veras.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Navegación */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <button className="btn btn-secondary" onClick={step === 0 ? () => navigate(base) : atras} disabled={saving}>
          {step === 0 ? 'Cancelar' : 'Atrás'}
        </button>
        {step < STEPS.length - 1 ? (
          <button className="btn btn-primary" onClick={siguiente}>Siguiente</button>
        ) : (
          <button className="btn btn-primary" onClick={crear} disabled={saving}>
            {saving ? 'Creando...' : 'Crear'}
          </button>
        )}
      </div>
    </div>
  )
}
